// ÁTTEKINTÉS — „Pénzügy" kártya: a számlázás három kérdése egy pillantásra (`invoices.view` mögött).
//
//   • LEJÁRT: darab + összeg — ez az egyetlen sor, ami cselekvést kér (behajtás), ezért kap hangsúlyt (danger),
//   • FIZETÉSRE VÁR: kiállított, még nem fizetett — darab + összeg,
//   • E HÓNAPBAN KIÁLLÍTVA: darab — a hónap tempójának mérője.
//
// AZ ÖSSZEG DEVIZA-BONTOTT: a számla-állomány több devizás (HUF + EUR), és a devizák ÖSSZEADÁSA hamis szám
// lenne (nincs itt átváltás — az az árfolyam-réteg dolga a bevétel-riporton). Ezért soronként annyi összeg
// jelenik meg, ahány deviza tényleg előfordul, a legnagyobbal kezdve.
//
// A sorok a MÁR SZŰRT számla-listára visznek (`?status=overdue` / `issued`) — sor-végi NYÍL NÉLKÜL
// (user-döntés): a hover-háttér az affordancia, a nyíl csak ismételte volna.
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { PanelCard } from '@/components/shared/panel-card';
import type { MoneySummary } from '@/features/dashboard/use-dashboard-data';
import { formatMoney } from '@/lib/format-money';

/** Egy sor: felirat + deviza-bontású összeg + darabszám, a szűrt listára mutató linkkel. */
function FinanceRow({
  label,
  summary,
  status,
  tone,
}: {
  label: string;
  summary: MoneySummary;
  status: string;
  tone?: 'danger';
}): JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <CardRow>
      <Link
        to="/invoices"
        search={{ status }}
        className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
      >
        <div className="min-w-0 flex-1">
          <div className="text-md text-foreground truncate">{label}</div>
          {summary.totals.length > 0 && (
            <div
              className={`text-xs tabular-nums ${tone === 'danger' ? 'text-danger-600' : 'text-muted-foreground'}`}
            >
              {summary.totals
                .map((s) => formatMoney(String(s.amount), s.currency, i18n.language))
                .join(' · ')}
            </div>
          )}
        </div>
        <span
          className={`text-md font-semibold tabular-nums ${tone === 'danger' ? 'text-danger-600' : 'text-foreground'}`}
        >
          {t('dashboard.finance.count', { count: summary.count })}
        </span>
      </Link>
    </CardRow>
  );
}

export interface FinanceCardProps {
  data: {
    overdue: MoneySummary;
    awaiting: MoneySummary;
    issuedThisMonth: number;
  } | null;
  loading: boolean;
}

export function FinanceCard({ data, loading }: FinanceCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <PanelCard
      capped
      title={t('dashboard.finance.title')}
      openAllTo="/invoices"
      loading={loading || data === null}
      skeletonRows={3}
    >
      {data !== null && (
        <CardRows>
          <FinanceRow
            label={t('dashboard.finance.overdue')}
            summary={data.overdue}
            status="overdue"
            {...(data.overdue.count > 0 ? { tone: 'danger' as const } : {})}
          />
          <FinanceRow
            label={t('dashboard.finance.awaiting')}
            summary={data.awaiting}
            status="issued"
          />
          <CardRow>
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-md text-foreground min-w-0 flex-1 truncate">
                {t('dashboard.finance.issuedThisMonth')}
              </span>
              <span className="text-md text-foreground font-semibold tabular-nums">
                {t('dashboard.finance.count', { count: data.issuedThisMonth })}
              </span>
            </div>
          </CardRow>
        </CardRows>
      )}
    </PanelCard>
  );
}
